import React, { useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Stack,
  Chip,
  useTheme,
  alpha,
  Grid,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Star as StarIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Tag as TagIcon,
  Reply as ReplyIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ReviewForm from './ReviewForm';
import ReportReviewButton from '../ReportReviewButton';

/**
 * Modal indépendante pour écrire une évaluation
 * Utilise le ReviewForm existant dans un contexte de modal
 */
const ReservationReviewModal = ({ 
  open, 
  onClose, 
  reservation,
  existingReview,
  editMode = false,
  onReviewSubmitted 
}) => {
  const { t, i18n, ready } = useTranslation('reviews', { useSuspense: false });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Détecter si la review est rejetée
  const isRejected = reservation?.reservationReview?.moderation_status === 'rejected';
  
  useEffect(() => {
    i18n.loadNamespaces('reviews');
  }, [i18n]);

  const handleReviewSuccess = (updatedReview) => {
    console.log('✅ handleReviewSuccess appelé avec:', updatedReview);
    
    // Extraire l'objet review si la structure est {message, data}
    const reviewData = updatedReview?.data || updatedReview;
    console.log('📦 Review data extraite:', reviewData);
    
    // Callback quand la review est soumise avec succès
    if (onReviewSubmitted) {
      onReviewSubmitted(reviewData || reservation);
    }
    console.log('🚪 Fermeture du modal...');
    onClose();
  };

  const startDate = useMemo(() => (reservation?.start_date ? new Date(reservation.start_date) : null), [reservation?.start_date]);
  const endDate = useMemo(() => (reservation?.end_date ? new Date(reservation.end_date) : null), [reservation?.end_date]);

  const formattedDateRange = useMemo(() => {
    if (!startDate || !endDate) return null;
    try {
      const formatter = new Intl.DateTimeFormat(i18n.language || 'fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
      return `${formatter.format(startDate)} → ${formatter.format(endDate)}`;
    } catch (error) {
      return `${startDate.toLocaleDateString()} → ${endDate.toLocaleDateString()}`;
    }
  }, [endDate, i18n.language, startDate]);

  const totalAmountDisplay = useMemo(() => {
    if (!reservation?.total_amount) return null;
    try {
      return new Intl.NumberFormat(i18n.language || 'fr-FR', {
        style: 'currency',
        currency: reservation?.currency || reservation?.listing?.currency || 'EUR'
      }).format(Number(reservation.total_amount));
    } catch (error) {
      return `${reservation.total_amount} ${reservation?.currency || ''}`;
    }
  }, [i18n.language, reservation?.currency, reservation?.listing?.currency, reservation?.total_amount]);

  const statusColorMap = {
    COMPLETED: 'success',
    CONFIRMED: 'info',
    ACTIVE: 'primary',
    PENDING: 'warning',
    CANCELLED: 'error'
  };

  const statusChipColor = statusColorMap[reservation?.status] || 'default';

  if (!reservation || !ready) return null;

  const statusLabelMap = {
    COMPLETED: t('reviews.modal.statusCompleted', 'Réservation terminée'),
    CONFIRMED: t('reviews.modal.statusConfirmed', 'Réservation confirmée'),
    ACTIVE: t('reviews.modal.statusActive', 'Réservation en cours'),
    PENDING: t('reviews.modal.statusPending', 'Réservation en attente'),
    CANCELLED: t('reviews.modal.statusCancelled', 'Réservation annulée')
  };

  const statusLabel = statusLabelMap[reservation?.status] || reservation?.status || t('reviews.modal.statusUnknown', 'Statut indisponible');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={isMobile}
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 4,
          backgroundColor: theme.palette.background.default,
          overflow: 'hidden',
          maxHeight: isMobile ? '100vh' : 'calc(100vh - 3rem)',
          my: isMobile ? 0 : 1.5
        }
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.darker || theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          color: 'white',
          position: 'relative',
          py: isMobile ? 1.5 : 2,
          px: isMobile ? 2 : 4,
          pr: isMobile ? 6 : 8
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: isMobile ? 8 : 16,
            top: isMobile ? 8 : 16,
            color: 'white',
            backgroundColor: 'rgba(255,255,255,0.15)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.25)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          {/* Partie gauche: Icône + Titre/Sous-titre */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.18)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <StarIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant={isMobile ? 'h6' : 'h5'} component="h2" fontWeight={700} lineHeight={1.2}>
                {t('reviews.writeReviewTitle', 'Évaluer votre expérience')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.25, fontSize: '0.85rem' }}>
                {t('reviews.modal.instructionsTitle', 'Partagez un retour détaillé sur votre location')}
              </Typography>
            </Box>
          </Box>

          {/* Partie droite: Chips dates et statut */}
          {!isMobile && (
            <Stack 
              direction="column" 
              spacing={0.75} 
              sx={{ 
                alignItems: 'flex-end',
                flexShrink: 0,
                mr: 2
              }}
            >
              <Chip
                icon={<CalendarIcon />}
                label={formattedDateRange || t('reviews.modal.dateUnknown', 'Dates indisponibles')}
                color="default"
                variant="outlined"
                size="small"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.18)',
                  color: 'white',
                  fontWeight: 500,
                  px: 1.2,
                  height: 26,
                  '& .MuiChip-icon': {
                    color: 'white',
                    fontSize: 14,
                    ml: 0.5
                  },
                  '& .MuiChip-label': {
                    fontSize: '0.75rem',
                    px: 0.5
                  }
                }}
              />
              <Chip
                label={statusLabel}
                color={statusChipColor}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.18)',
                  color: 'white',
                  fontWeight: 600,
                  px: 1.2,
                  height: 26,
                  '& .MuiChip-label': {
                    fontSize: '0.75rem',
                    px: 0.5
                  }
                }}
              />
            </Stack>
          )}
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${theme.palette.background.default} 35%)`,
          overflowY: isMobile ? 'auto' : 'hidden'
        }}
      >
        <Box sx={{ px: isMobile ? 2 : 4, py: isMobile ? 2 : 2.5 }}>
          <Grid container spacing={isMobile ? 2 : 3} alignItems="stretch">
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3
                }}
              >
                <Box
                  sx={{
                    p: isMobile ? 2 : 3,
                    borderRadius: 3,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                    boxShadow: theme.shadows[isMobile ? 1 : 3]
                  }}
                >
                  <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', fontWeight: 600, mb: 2, color: theme.palette.primary.dark }}>
                    {t('reviews.modal.summaryTitle', 'Résumé de la réservation')}
                  </Typography>

                  <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {t('reviews.modal.listingTitle', 'Équipement concerné')}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 0.5 }}>
                        {reservation.listing?.title || t('reviews.modal.listingUnknown', 'Titre indisponible')}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: 2,
                          backgroundColor: alpha(theme.palette.primary.main, 0.12),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: theme.palette.primary.main
                        }}
                      >
                        <CalendarIcon />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {t('reviews.modal.dateRange', 'Période de location')}
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {formattedDateRange || t('reviews.modal.dateUnknown', 'Dates indisponibles')}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: 2,
                          backgroundColor: alpha(theme.palette.success.main, 0.12),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: theme.palette.success.main
                        }}
                      >
                        <MoneyIcon />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {t('reviews.modal.amount', 'Montant total payé')}
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {totalAmountDisplay || t('reviews.modal.amountUnknown', 'Non communiqué')}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: 2,
                          backgroundColor: alpha(theme.palette.info.main, 0.12),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: theme.palette.info.main
                        }}
                      >
                        <TagIcon />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {t('reviews.modal.reservationId', 'Identifiant de réservation')}
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                          {reservation.id}
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    p: isMobile ? 2 : 3,
                    borderRadius: 3,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                    boxShadow: theme.shadows[isMobile ? 0 : 1]
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    {t('reviews.modal.instructionsHeading', 'Conseils pour un avis utile')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {t('reviews.modal.instructionsBody', 'Mentionnez les points forts, les aspects à améliorer et soyez honnête pour aider futurs locataires et propriétaires.')}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  height: isMobile ? 'auto' : '76vh',
                  overflowY: 'auto',
                  pr: isMobile ? 0 : 1,
                  pb: isMobile ? 0 : 0.5,
                  '&::-webkit-scrollbar': {
                    width: 8
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.25),
                    borderRadius: 4
                  }
                }}
              >
                <Box
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 3,
                    boxShadow: theme.shadows[isMobile ? 1 : 4],
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    p: isMobile ? 2 : 3,
                    minHeight: isMobile ? 'auto' : '100%',
                    '& textarea': {
                      minHeight: 140,
                      fontSize: '1rem',
                      padding: '12px',
                      lineHeight: 1.6
                    },
                    '& input, & select': {
                      minHeight: 44,
                      fontSize: '1rem'
                    },
                    '& button': {
                      minHeight: 42,
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: '0.95rem'
                    }
                  }}
                >
                  {/* Alerte si la review est rejetée - Positionnée en haut */}
                  {isRejected && (
                    <Box
                      sx={{
                        mb: 3,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.error.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.1)}`
                      }}
                    >
                      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            backgroundColor: alpha(theme.palette.error.main, 0.15),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600} color="error.main" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                            {t('reviews.modal.reviewRejected', 'Évaluation rejetée')}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: theme.palette.text.secondary,
                              lineHeight: 1.6,
                              fontSize: '0.85rem'
                            }}
                          >
                            {t('reviews.modal.reviewRejectedMessage', 'Votre évaluation a été rejetée par un modérateur et ne peut pas être resoumise. Vous pouvez consulter son contenu ci-dessous.')}
                          </Typography>
                          {reservation.reservationReview?.moderator_notes && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: theme.palette.text.secondary,
                                lineHeight: 1.6,
                                fontSize: '0.85rem',
                                mt: 1,
                                fontStyle: 'italic',
                                pl: 1,
                                borderLeft: `3px solid ${alpha(theme.palette.error.main, 0.4)}`
                              }}
                            >
                              <strong>{t('reviews.modal.moderationComment', 'Raison :')} </strong>
                              {reservation.reservationReview.moderator_notes}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  )}

                  <ReviewForm
                    reservationId={reservation.id}
                    listingId={reservation.listing_id}
                    existingReview={existingReview}
                    editMode={editMode}
                    isRejected={isRejected}
                    onSuccess={handleReviewSuccess}
                    onCancel={onClose}
                  />
                  
                  {/* Réponse du propriétaire si elle existe */}
                  {existingReview?.owner_response && (() => {
                    console.log('🔍 [ReservationReviewModal] response_moderation_status:', existingReview.response_moderation_status);
                    return (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: alpha(theme.palette.info.main, 0.08),
                          border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.info.main, 0.1)}`
                        }}
                      >
                      <Stack direction="row" alignItems="center" spacing={1} mb={1.5} flexWrap="wrap">
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            backgroundColor: alpha(theme.palette.info.main, 0.15),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <ReplyIcon sx={{ fontSize: 16, color: theme.palette.info.main }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" fontWeight={600} color="info.main" sx={{ fontSize: '0.9rem' }}>
                            {t('reviews.modal.ownerResponse', 'Réponse du propriétaire')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {new Date(existingReview.owner_response_date).toLocaleDateString(i18n.language || 'fr-FR', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </Typography>
                        </Box>
                        
                        {/* Badge de statut de modération de la réponse */}
                        {(existingReview.response_moderation_status === 'pending' || existingReview.response_moderation_status === null) && (
                          <Chip
                            label={t('reviews.modal.responsePending', 'En attente de modération')}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              backgroundColor: alpha(theme.palette.warning.main, 0.12),
                              color: theme.palette.warning.dark,
                              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                            }}
                          />
                        )}
                        
                        {existingReview.response_moderation_status === 'approved' && (
                          <Chip
                            label={t('reviews.modal.responseApproved', 'Approuvé')}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              backgroundColor: alpha(theme.palette.success.main, 0.12),
                              color: theme.palette.success.dark,
                              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                            }}
                          />
                        )}
                        
                        {existingReview.response_moderation_status === 'rejected' && (
                          <Chip
                            label={t('modal.responseRejected', 'Rejeté')}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              backgroundColor: alpha(theme.palette.error.main, 0.12),
                              color: theme.palette.error.dark,
                              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`
                            }}
                          />
                        )}
                        
                        {existingReview.response_moderation_status === 'changes_requested' && (
                          <Chip
                            label={t('reviews.modal.responseChangesRequested', 'Modifications demandées')}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              backgroundColor: alpha(theme.palette.warning.main, 0.12),
                              color: theme.palette.warning.dark,
                              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                            }}
                          />
                        )}
                        
                        {/* Bouton de signalement de la réponse */}
                        <ReportReviewButton
                          reviewType="owner_response"
                          reviewId={existingReview.id}
                          variant="icon"
                          size="small"
                        />
                      </Stack>
                      
                      {/* Message de modération si modifications demandées ou rejet */}
                      {(existingReview.response_moderation_status === 'changes_requested' || 
                        existingReview.response_moderation_status === 'rejected') && 
                        existingReview.response_moderation_notes && (
                        <Box
                          sx={{
                            mb: 1.5,
                            p: 1.5,
                            borderRadius: 1,
                            backgroundColor: existingReview.response_moderation_status === 'rejected' 
                              ? alpha(theme.palette.error.main, 0.08)
                              : alpha(theme.palette.warning.main, 0.08),
                            border: `1px solid ${existingReview.response_moderation_status === 'rejected' 
                              ? alpha(theme.palette.error.main, 0.25)
                              : alpha(theme.palette.warning.main, 0.25)}`
                          }}
                        >
                          <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                            💬 {t('reviews.modal.moderationComment', 'Message de modération :')}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: theme.palette.text.secondary,
                              fontSize: '0.75rem',
                              fontStyle: 'italic'
                            }}
                          >
                            {existingReview.response_moderation_notes}
                          </Typography>
                        </Box>
                      )}
                      
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          lineHeight: 1.6,
                          fontStyle: 'italic',
                          fontSize: '0.9rem',
                          pl: 1,
                          borderLeft: `3px solid ${alpha(theme.palette.info.main, 0.4)}`
                        }}
                      >
                        {existingReview.owner_response}
                      </Typography>
                    </Box>
                    );
                  })()}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReservationReviewModal;